import {PolymerElement, html} from '@polymer/polymer';
import {htmlLiteral} from '@polymer/polymer/lib/utils/html-tag';
import '@polymer/polymer/lib/elements/dom-repeat.js';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {FieldcontainerSharedStylesHTML} from '../shared-styles/field-container.js';
import {backgroundColorValue, desiredTextColor} from '../../javascripts/modules/colors.js';

class KeliMultiColumn extends ScopeBinding(PolymerElement) {
  static get template() {
    return html`
${FieldcontainerSharedStylesHTML}
<style>
:host {
  display: flex;
  justify-content: space-between;
  padding: var(--keli-multi-column-padding, 0);
  background-color: var(--keli-multi-column-background-color, transparent);
  width: 100%;
}

.region {
  flex: 1;
  margin: 0 10px;
  overflow: visible;
  display: grid;
  grid-template-columns: 1fr;
  align-content: start;
}

.region:first-of-type {
  margin-left: 0;
}

.region:last-of-type {
  margin-right: 0;
}

${this.columnStyles}
</style>

<dom-repeat items="[[slotDefinition]]">
  <template>
    <div class="region" slot-name$="[[item]]"><slot name="[[item]]"></slot></div>
  </template>
</dom-repeat>`;
  }

  static get columnStyles() {
    const styles = [];
    for (let i = 1; i <= 8; ++i) {
      let word = numberToWords.toWords(i);
      styles.push(`.region[slot-name="${word}"] {
  flex: var(--keli-multi-column-slot-${word}-flex, 1);
  overflow: var(--keli-multi-column-slot-${word}-overflow, visible);
  padding: var(--keli-multi-column-slot-${word}-padding, 0);
  max-width: var(--keli-multi-column-slot-${word}-max-width, none);
  align-content: var(--keli-multi-column-slot-${word}-align-content, start);
}`);
    }
    const literal = [styles.join('\n')];
    literal.raw = [styles.join('\n')];
    return htmlLiteral(literal);
  }

  static get is() {
    return 'keli-multi-column';
  }

  static get properties() {
    return {
      background: {type: String, scopevar: true, observer: 'backgroundChanged'},
      columns: {type: Number, scopevar: true, observer: 'columnsChanged'},
      containsRegions: {type: Boolean, value: true},
      finalPadding: {type: Number, computed: 'computeFinalPadding(padding, background)'},
      flex: {type: Array, scopevar: true, observer: 'flexChanged'},
      isBlockPanelElement: {type: Boolean, value: true},
      isRegionElement: {type: Boolean, value: true},
      keliControlTypes: {type: Array, value: ['layout-multi-column']},
      padding: {type: Array, scopevar: true},
      slotDefinition: Array,
      widths: {type: Array, scopevar: true, observer: 'widthsChanged'},
      overflow: {type: Array, scopevar: true, observer: 'overflowChanged'},
      alignContent: {type: Array, scopevar: true, observer: 'alignContentChanged'},
    }
  }

  getWordList(max = 8) {
    const words = [];
    let current = 1;
    while (current <= max) {
      words.push(numberToWords.toWords(current));
      ++current;
    }
    return words;
  }

  backgroundChanged(newValue) {
    const backgroundColor = backgroundColorValue(newValue);
    const darkMode = (desiredTextColor(backgroundColor, true) === '#FFFFFF');
    this.updateStyles(Object.assign(KeliInterface.Styles.getStyleModeVariables(darkMode), {
      '--keli-multi-column-background-color': backgroundColor
    }));
  }

  columnsChanged(newValue) {
    this.slotDefinition = this.getWordList().slice(0, newValue || 0);
  }

  computeFinalPadding(padding, background) {
    let defaultPadding = (background ? 10 : undefined);
    let styleObject = {};
    const words = this.getWordList();
    if (padding && Array.isArray(padding)) {
      padding.forEach((value, index) => {
        if (!value) {
          return;
        }
        styleObject[`--keli-multi-column-slot-${words[index]}-padding`] = `${parseInt(value, 10)}px`;
      });
    } else if (padding) {
      defaultPadding = parseInt(padding, 10);
    }

    if (defaultPadding) {
      styleObject[`--keli-multi-column-padding`] = `${defaultPadding}px`;
    }
    this.updateStyles(styleObject);
    return defaultPadding;
  }

  flexChanged(newValue) {
    const words = this.getWordList();
    if (newValue && Array.isArray(newValue)) {
      let styleObject = {};
      newValue.forEach((value, index) => {
        if (!value) {
          return;
        }
        styleObject[`--keli-multi-column-slot-${words[index]}-flex`] = value;
      });
      this.updateStyles(styleObject);
    }
  }

  widthsChanged(newValue) {
    const words = this.getWordList();
    if (newValue && Array.isArray(newValue)) {
      let styleObject = {};
      newValue.forEach((value, index) => {
        if (!value) {
          return;
        }
        const allowedFlexibleWidths = ['min-content', 'max-content', 'fit-content', 'auto'];
        if (!allowedFlexibleWidths.includes(value)) {
          value = `${parseInt(value, 10)}px`;
        }
        styleObject[`--keli-multi-column-slot-${words[index]}-flex`] = `0 0 ${value}`;
        styleObject[`--keli-multi-column-slot-${words[index]}-max-width`] = `${value}`;
      });
      this.updateStyles(styleObject);
    }
  }

  overflowChanged(newValue) {
    const words = this.getWordList();
    if (newValue && Array.isArray(newValue)) {
      let styleObject = {};
      newValue.forEach((value, index) => {
        if (!value) {
          return;
        }
        styleObject[`--keli-multi-column-slot-${words[index]}-overflow`] = value;
      });
      this.updateStyles(styleObject);
    }
  }

  alignContentChanged(newValue) {
    const words = this.getWordList();
    if (newValue && Array.isArray(newValue)) {
      let styleObject = {};
      newValue.forEach((value, index) => {
        if (!value) {
          return;
        }
        styleObject[`--keli-multi-column-slot-${words[index]}-align-content`] = value;
      });
      this.updateStyles(styleObject);
    }
  }
}

customElements.define(KeliMultiColumn.is, KeliMultiColumn);
export {KeliMultiColumn};
